import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Button } from '../../platform/primitives/Button'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { AmountCell, DateCell, MerchantCell, rowName, Unavailable } from './ruleRowCells'
import { usePutBack, useRuleExclusions, type RuleExclusion } from './useRuleApply'
import { useRuleRowTxns, type RuleRowTxn } from './useRuleRowTxns'

export const REMOVED_EMPTY = "You haven't removed any transactions from this rule."
export const PUT_BACK_DONE = "Put back. The rule can apply to it again, but nothing changes until you apply it."

const LABEL = 'Transactions removed from this rule'

const nameOf = (t: RuleRowTxn) => rowName(t, 'this transaction')

/**
 * "Removed": the transactions taken off this rule for good, newest first, each with **Put back**.
 * Put back only lifts the exclusion; it never re-applies the rule (Apply now or Apply to selected
 * does), and the toast says so.
 */
export function RuleRemoved({ ruleId }: { ruleId: string }) {
  const { state, hasMore, loadingMore, loadMore } = useRuleExclusions(ruleId)
  const rows = state.status === 'success' ? state.data.exclusions : []
  const lookup = useRuleRowTxns(rows.map((e) => e.transactionId))
  const phone = useBreakpoint() === 'phone'
  const putBack = usePutBack(ruleId)
  const toast = useToast()

  const onPutBack = (id: string) => {
    if (putBack.isPending) return
    putBack.mutate(id, {
      onSuccess: () => toast.show({ kind: 'success', message: PUT_BACK_DONE }),
      onError: () => toast.show({ kind: 'error', message: "Couldn't put it back. Nothing was changed. Try again in a moment." }),
    })
  }

  const action = (e: RuleExclusion) => {
    const busy = putBack.isPending && putBack.variables === e.transactionId
    return (
      <span className="rule-applied__actions">
        <Button
          variant="outline"
          size="panel"
          aria-label={`Put back ${nameOf(lookup(e.transactionId))}`}
          aria-disabled={putBack.isPending || undefined}
          aria-busy={busy || undefined}
          onClick={() => onPutBack(e.transactionId)}
        >
          {busy ? 'Putting back…' : 'Put back'}
        </Button>
      </span>
    )
  }

  const columns: Column<RuleExclusion>[] = [
    {
      key: 'date',
      header: 'Date',
      width: 'minmax(0, 0.7fr)',
      render: (e) => <DateCell t={lookup(e.transactionId)} />,
    },
    { key: 'merchant', header: 'Merchant', width: 'minmax(0, 1.6fr)', render: (e) => (
        <>
          <MerchantCell id={e.transactionId} t={lookup(e.transactionId)} snapshot={null} />
          <Unavailable t={lookup(e.transactionId)} />
        </>
      ),
    },
    { key: 'amount', header: 'Amount', width: 'minmax(0, 0.9fr)', align: 'end', render: (e) => <AmountCell t={lookup(e.transactionId)} /> },
    { key: 'action', header: 'Action', width: 'minmax(0, 0.9fr)', align: 'end', render: action },
  ]

  return (
    <Async
      surface="rule-removed"
      state={state}
      skeleton={
        <>
          <Skeleton height={44} />
          <Skeleton height={44} />
        </>
      }
      empty={{ title: REMOVED_EMPTY }}
    >
      {({ exclusions }) => (
        <>
          {phone ? (
            <ul className="rule-applied__cards" aria-label={LABEL}>
              {exclusions.map((e) => {
                const t = lookup(e.transactionId)
                return (
                  <li key={e.transactionId} className="rule-applied__card">
                    <div className="rule-applied__card-top">
                      <MerchantCell id={e.transactionId} t={t} snapshot={null} />
                      <AmountCell t={t} />
                    </div>
                    <Unavailable t={t} />
                    <DateCell t={t} />
                    {action(e)}
                  </li>
                )
              })}
            </ul>
          ) : (
            <DataGrid columns={columns} groups={[{ label: '', rows: exclusions }]} rowKey={(e) => e.transactionId} label={LABEL} empty={REMOVED_EMPTY} />
          )}
          {hasMore && (
            <div className="rule-applied__more">
              <Button variant="outline" size="panel" aria-disabled={loadingMore || undefined} onClick={loadingMore ? undefined : loadMore}>
                {loadingMore ? 'Loading…' : 'Show more'}
              </Button>
            </div>
          )}
        </>
      )}
    </Async>
  )
}
